import { useEffect, useState, type ComponentType, type ReactNode, type SVGProps } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  ROLE_LABELS,
  isOverLimit,
  limitOf,
  usageOf,
  type FeatureKey,
  type ModuleCode,
  type Permission,
} from '@azko/shared';
import { useSession } from '../lib/session.js';
import { api } from '../lib/api.js';
import { InstallAppButton, usePwa } from '../pwa/pwa.js';
import { MadeByAzkoGroup, cx } from '../components/ui.js';
import { CondosMark, CondosWordmark } from '../components/brand.js';
import {
  IconBuilding,
  IconClose,
  IconConcierge,
  IconDashboard,
  IconLayers,
  IconHistory,
  IconInbox,
  IconLogout,
  IconMenu,
  IconPackage,
  IconScan,
  IconSparkle,
  IconUsers,
} from '../components/icons.js';

/**
 * Layout do produto: sidebar fixa no desktop, gaveta sobreposta no mobile.
 *
 * O menu e montado a partir dos entitlements. Um condominio no plano
 * Essencial simplesmente nao ve "Reservas" — melhor do que ver e receber
 * "voce nao tem acesso" ao clicar.
 *
 * A shell e dividida em tres faixas com papeis distintos: sidebar (navegar),
 * topbar (contexto: em qual condominio estou, quem sou eu) e main (a tarefa).
 * Nenhum elemento de chrome mora dentro do main — assim as paginas so precisam
 * se preocupar com o proprio conteudo.
 */

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

interface NavItem {
  to: string;
  label: string;
  icon: IconComponent;
  /**
   * Rotulo da barra inferior do mobile, onde cabem ~9 caracteres. Sem ele o
   * item nao entra na barra — e o jeito de dizer "este destino nao e do dia a
   * dia da portaria".
   */
  short?: string;
  module: ModuleCode;
  feature?: FeatureKey;
  /**
   * Esconde o item de quem nao tem a permissao. Modulo e feature respondem "o
   * plano inclui?"; isto responde "o cargo permite?" — as duas perguntas que o
   * `authorize()` faz no backend, e pela mesma lista.
   */
  permission?: Permission;
}

interface NavGroup {
  /** `null` no primeiro grupo: rotulo sobre um item so seria ruido. */
  label: string | null;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: null,
    items: [{ to: '/', label: 'Visao geral', icon: IconDashboard, module: 'core', short: 'Inicio' }],
  },
  {
    label: 'Encomendas',
    items: [
      { to: '/encomendas', label: 'Todas as encomendas', icon: IconPackage, module: 'packages', short: 'Encomendas' },
      { to: '/registrar', label: 'Registrar entrada', icon: IconInbox, module: 'packages', short: 'Registrar' },
      {
        to: '/ler-qr',
        label: 'Ler QR de retirada',
        short: 'Ler QR',
        icon: IconScan,
        module: 'packages',
        feature: 'packages.qr_pickup',
      },
    ],
  },
  {
    label: 'Condominio',
    items: [
      { to: '/moradores', label: 'Moradores', icon: IconUsers, module: 'core' },
      {
        to: '/porteiros',
        label: 'Porteiros',
        icon: IconConcierge,
        module: 'core',
        permission: 'condominio:porteiros:visualizar',
      },
      {
        to: '/unidades',
        label: 'Blocos e unidades',
        icon: IconBuilding,
        module: 'core',
        // O porteiro ve as unidades no formulario de entrada, mas nao e ele
        // quem monta o predio.
        permission: 'condominio:unidades:criar',
      },
      {
        to: '/armarios',
        label: 'Armarios',
        icon: IconLayers,
        module: 'core',
        // O porteiro escolhe o armario no formulario de entrada, mas nao e ele
        // quem decide quais existem.
        permission: 'condominio:unidades:criar',
      },
      {
        to: '/historico',
        label: 'Historico',
        icon: IconHistory,
        module: 'core',
        permission: 'condominio:logs:visualizar',
      },
    ],
  },
];

/** Marca no topo do menu — o unico lugar do app onde ela aparece. */
function Logo() {
  return (
    <div className="flex items-center gap-3">
      <span className="grid size-10 shrink-0 place-items-center rounded-2xl bg-accent text-on-accent">
        <CondosMark className="h-5.5 w-5" />
      </span>
      <CondosWordmark height="1.05rem" />
    </div>
  );
}

function NavList({ onNavigate }: { onNavigate?: () => void }) {
  const { hasModuleAccess, can, allows } = useSession();

  const groups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter(
      (item) =>
        hasModuleAccess(item.module) &&
        (!item.feature || can(item.feature)) &&
        (!item.permission || allows(item.permission)),
    ),
  })).filter((group) => group.items.length > 0);

  return (
    <nav className="space-y-6">
      {groups.map((group, index) => (
        <div key={group.label ?? `group-${index}`}>
          {group.label && (
            <p className="mb-2 px-3 text-[0.68rem] font-bold uppercase tracking-[0.14em] text-faint">
              {group.label}
            </p>
          )}
          <ul className="space-y-0.5">
            {group.items.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === '/'}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    cx(
                      'group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition',
                      isActive
                        ? 'bg-accent/10 text-fg'
                        : 'text-muted hover:bg-surface hover:text-fg',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      {/* Barra de ativo: marca a linha inteira sem fechar o item
                          numa borda, que competia com a borda dos cards. */}
                      <span
                        aria-hidden
                        className={cx(
                          'absolute left-0 top-1/2 h-5 w-0.75 -translate-y-1/2 rounded-r-full bg-accent transition-opacity',
                          isActive ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      <item.icon
                        className={cx(
                          'size-[1.15rem] shrink-0 transition-colors',
                          isActive ? 'text-accent' : 'text-faint group-hover:text-muted',
                        )}
                      />
                      <span className="truncate">{item.label}</span>
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

/**
 * Consumo do periodo no rodape do menu.
 *
 * Fica sempre visivel de proposito: o limite do plano e a conversa comercial
 * do produto, e descobrir que estourou so na hora de registrar a encomenda e
 * a pior hora possivel.
 */
function PlanMeter({ onNavigate }: { onNavigate?: () => void }) {
  const { entitlements, allows } = useSession();
  if (!entitlements) return null;

  // O consumo do periodo interessa a quem opera a portaria — e ele que vai
  // esbarrar no teto. Ja preco e status da conta sao assunto de quem
  // administra: sem a permissao, o medidor continua, mas deixa de ser um link
  // para a tela de plano (que a API tambem recusa).
  const canManagePlan = allows('condominio:assinatura:visualizar');

  const limit = limitOf(entitlements, 'packages.per_month');
  const used = usageOf(entitlements, 'packages.per_month');
  const unlimited = limit === null;
  const over = isOverLimit(entitlements, 'packages.per_month');
  const percent = unlimited ? 0 : Math.min(100, Math.round((used / Math.max(limit, 1)) * 100));

  const body = (
    <>
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-accent">
          <IconSparkle className="size-3.5" />
          {entitlements.planName}
        </span>
        {canManagePlan && <span className="text-xs font-semibold text-faint">Gerenciar</span>}
      </div>

      {unlimited ? (
        <p className="mt-2 text-sm text-muted">Encomendas ilimitadas</p>
      ) : (
        <>
          <p className="mt-2 text-sm text-muted">
            <span className={cx('font-bold tabular-nums', over ? 'text-danger' : 'text-fg')}>
              {used}
            </span>
            <span className="tabular-nums"> de {limit}</span> encomendas
          </p>
          <div
            className="mt-2 h-1.5 overflow-hidden rounded-pill bg-surface-2"
            role="progressbar"
            aria-valuenow={percent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Consumo do plano"
          >
            <div
              className={cx(
                'h-full rounded-pill transition-all',
                over ? 'bg-danger' : percent >= 80 ? 'bg-warn' : 'bg-accent',
              )}
              style={{ width: `${Math.max(percent, 2)}%` }}
            />
          </div>
        </>
      )}
    </>
  );

  const boxClass = 'block rounded-card border border-line bg-surface p-4';

  if (!canManagePlan) return <div className={boxClass}>{body}</div>;

  return (
    <NavLink
      to="/plano"
      onClick={onNavigate}
      className={cx(boxClass, 'transition hover:border-faint')}
    >
      {body}
    </NavLink>
  );
}

function UserChip({ className }: { className?: string }) {
  const { session } = useSession();
  const navigate = useNavigate();
  const [leaving, setLeaving] = useState(false);

  if (!session) return null;

  const role = session.active ? ROLE_LABELS[session.active.role] : null;
  const detail = [role, session.active?.shift].filter(Boolean).join(' • ') || session.user.email;

  async function handleLogout() {
    setLeaving(true);
    try {
      await api.post('/auth/logout');
    } finally {
      // Recarrega em vez de so limpar o cache: garante que nenhum dado do
      // tenant anterior sobreviva em memoria.
      navigate('/', { replace: true });
      window.location.reload();
    }
  }

  return (
    <div className={cx('flex items-center gap-3', className)}>
      <span className="grid size-9 shrink-0 place-items-center rounded-full bg-surface-2 text-xs font-bold">
        {session.user.initials}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-bold">{session.user.name}</span>
        <span className="block truncate text-xs text-muted">{detail}</span>
      </span>
      <button
        type="button"
        onClick={handleLogout}
        disabled={leaving}
        title="Sair"
        aria-label="Sair"
        className="shrink-0 rounded-lg p-2 text-faint transition hover:bg-surface-2 hover:text-fg disabled:opacity-50"
      >
        <IconLogout className="size-[1.15rem]" />
      </button>
    </div>
  );
}

/** Conteudo da sidebar — reaproveitado pela gaveta do mobile. */
/** Conteudo da sidebar — reaproveitado pela gaveta do mobile. */
function SidebarContent({ onNavigate, onClose }: { onNavigate?: () => void; onClose?: () => void }) {
  return (
    <>
      <div className="flex shrink-0 items-center justify-between px-5 py-6">
        <Logo />
        {/* So na gaveta. No celular a barra de baixo fica ATRAS do overlay
            enquanto ela esta aberta, entao sem este botao o unico jeito de
            fechar seria acertar a faixa de fundo ao lado dela. */}
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar menu"
            className="-mr-1 rounded-xl p-2 text-muted transition hover:bg-surface hover:text-fg"
          >
            <IconClose className="size-5" />
          </button>
        )}
      </div>

      {/* Only o menu rola: logo e rodape ficam ancorados mesmo com 8 modulos. */}
      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-4">
        <NavList onNavigate={onNavigate} />
      </div>

      <div className="shrink-0 space-y-3 border-t border-line-soft p-4">
        <InstallAppButton />
        <PlanMeter onNavigate={onNavigate} />
        <UserChip className="px-1" />
        <MadeByAzkoGroup className="px-1 pt-1" />
      </div>
    </>
  );
}

/** Contexto do tenant — o que a topbar existe para responder. */
function CondominiumBadge() {
  const { session } = useSession();
  const { isOnline } = usePwa();
  const name = session?.active?.condominium.name;
  if (!name) return null;

  return (
    <span className="flex min-w-0 items-center gap-2.5">
      <span
        aria-hidden
        className={cx('size-1.5 shrink-0 rounded-full', isOnline ? 'bg-accent' : 'bg-warn')}
      />
      <span className="truncate text-sm font-bold">{name}</span>
      <span className={cx('hidden shrink-0 text-xs sm:inline', isOnline ? 'text-faint' : 'text-warn')}>
        {isOnline ? 'Portaria conectada' : 'Sem conexão'}
      </span>
    </span>
  );
}

/**
 * Barra inferior — a navegacao de verdade do celular.
 *
 * O porteiro trabalha de pe, com uma mao, o telefone segurado por baixo e uma
 * caixa na outra. A faixa que o polegar alcanca sem reposicionar o aparelho e
 * a de BAIXO — e era exatamente onde nao havia nada: as tres telas do dia a
 * dia (registrar, ler QR, ver a lista) moravam atras de um botao no canto
 * superior esquerdo, o ponto mais longe do polegar que existe numa tela.
 *
 * A gaveta continua inteira, com os oito destinos, o medidor de plano e a
 * conta. O que muda e que ela deixa de ser o unico caminho para o que se usa
 * o tempo todo.
 *
 * Entram aqui so os itens com `short` — o rotulo de barra nao e o do menu
 * ("Ler QR de retirada" nao cabe em 72px). E a mesma filtragem por plano e
 * permissao do menu: quem nao tem `packages.qr_pickup` ve quatro itens, nao um
 * botao morto.
 */
function BottomNav({ onOpenMenu }: { onOpenMenu: () => void }) {
  const { hasModuleAccess, can, allows } = useSession();

  const items = NAV_GROUPS.flatMap((group) => group.items).filter(
    (item) =>
      item.short &&
      hasModuleAccess(item.module) &&
      (!item.feature || can(item.feature)) &&
      (!item.permission || allows(item.permission)),
  );

  if (items.length === 0) return null;

  const cellClass =
    'flex h-full w-full flex-col items-center justify-center gap-1 px-1 pb-1 pt-2 text-[0.65rem] font-bold';

  return (
    // `pb-[env(safe-area-inset-bottom)]` por causa da faixa de gestos do
    // iPhone: sem ela a linha de rotulos fica debaixo do risco branco.
    <nav
      aria-label="Atalhos"
      className="fixed inset-x-0 bottom-0 z-30 border-t border-line-soft bg-ink/95 pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)] backdrop-blur lg:hidden"
    >
      <ul className="flex items-stretch">
        {items.map((item) => (
          <li key={item.to} className="min-w-0 flex-1">
            <NavLink
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                cx(cellClass, 'relative transition', isActive ? 'text-accent' : 'text-faint')
              }
            >
              {({ isActive }) => (
                <>
                  {/* O tracinho no topo da celula, e nao a celula inteira
                      pintada: o item ativo precisa se distinguir sem virar um
                      bloco de cor na barra mais estreita da tela. */}
                  <span
                    aria-hidden
                    className={cx(
                      'absolute inset-x-3 top-0 h-0.5 rounded-b-full bg-accent transition-opacity',
                      isActive ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <item.icon className="size-5 shrink-0" />
                  <span className="w-full truncate text-center">{item.short}</span>
                </>
              )}
            </NavLink>
          </li>
        ))}

        <li className="min-w-0 flex-1">
          <button
            type="button"
            onClick={onOpenMenu}
            aria-label="Abrir menu"
            className={cx(cellClass, 'text-faint transition active:text-fg')}
          >
            <IconMenu className="size-5 shrink-0" />
            <span className="w-full truncate text-center">Menu</span>
          </button>
        </li>
      </ul>
    </nav>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();

  // Navegar fecha a gaveta — sem isto o menu fica aberto sobre a pagina nova.
  useEffect(() => setMenuOpen(false), [pathname]);

  // Esc fecha, e o body trava o scroll enquanto a gaveta esta sobreposta.
  useEffect(() => {
    if (!menuOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  return (
    // minmax(0,1fr) na coluna do conteudo: sem isso uma tabela larga estica a
    // grid e empurra a sidebar para fora da tela.
    <div className="safe-screen lg:grid lg:grid-cols-[17rem_minmax(0,1fr)]">
      {/* Sidebar — desktop */}
      <aside className="safe-area-y sticky top-0 hidden h-dvh flex-col border-r border-line-soft bg-sidebar lg:flex">
        <SidebarContent />
      </aside>

      {/* Gaveta — mobile. Sobrepoe em vez de empurrar o conteudo. */}
      <div
        className={cx(
          'fixed inset-0 z-40 lg:hidden',
          menuOpen ? 'pointer-events-auto' : 'pointer-events-none',
        )}
        aria-hidden={!menuOpen}
        inert={!menuOpen}
      >
        <button
          type="button"
          tabIndex={menuOpen ? 0 : -1}
          aria-label="Fechar menu"
          onClick={() => setMenuOpen(false)}
          className={cx(
            'absolute inset-0 bg-ink/70 backdrop-blur-sm transition-opacity',
            menuOpen ? 'opacity-100' : 'opacity-0',
          )}
        />
        <div
          className={cx(
            'safe-area-y absolute inset-y-0 left-0 flex w-68 max-w-[85vw] flex-col border-r border-line-soft bg-sidebar transition-transform duration-200',
            menuOpen ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          <SidebarContent
            onNavigate={() => setMenuOpen(false)}
            onClose={() => setMenuOpen(false)}
          />
        </div>
      </div>

      <div className="safe-screen flex flex-col">
        {/* Topbar — a faixa de contexto, acima de qualquer conteudo de pagina.
            No celular ela carrega UMA coisa: em qual condominio estou. O
            hamburguer desceu para a barra de baixo e o logotipo saiu — dentro
            do app ele nao informa nada a quem ja entrou, e estava disputando
            largura com o nome do predio, que e o unico dado da faixa. */}
        <header className="safe-area-top sticky top-0 z-30 border-b border-line-soft bg-ink/80 backdrop-blur">
          <div className="mx-auto flex h-14 max-w-360 items-center gap-3 pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] sm:h-16 sm:pl-[max(2rem,env(safe-area-inset-left))] sm:pr-[max(2rem,env(safe-area-inset-right))]">
            <div className="flex min-w-0 flex-1">
              <CondominiumBadge />
            </div>
          </div>
        </header>

        {/* Conteudo. O space-y define o ritmo vertical de TODAS as paginas —
            elas nao precisam mais abrir cada bloco com um mt-4 avulso.

            O `pb-24` do celular e o espaco da barra inferior: ela e `fixed`, e
            sem isto o ultimo cartao de qualquer lista termina debaixo dela. */}
        <main className="mx-auto w-full max-w-360 flex-1 space-y-5 pb-24 pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] pt-5 sm:space-y-6 sm:pb-8 sm:pl-[max(2rem,env(safe-area-inset-left))] sm:pr-[max(2rem,env(safe-area-inset-right))] sm:pt-8 lg:pb-8">
          {children}
        </main>
      </div>

      <BottomNav onOpenMenu={() => setMenuOpen(true)} />
    </div>
  );
}
